import { Vec3, dot, length, normalize } from '../math/vec3'
import { rotate } from '../math/quat'
import { CelestialBody } from '../world/body'
import { densityAt } from '../world/atmosphere'
import { Vessel } from '../vessel/vessel'
import { PartCatalog, partDef } from '../vessel/catalog'
import { shapeRadius } from '../vessel/extents'
import { ALLUMINIO } from '../vessel/part'

/** Costante di Sutton e Graves per l'aria, SI. */
const K_SUTTON = 1.7415e-4
const STEFAN = 5.670374e-8
const EMISSIVITA = 0.8
/** Calore specifico medio della struttura, J/(kg·K). */
const CALORE_SPECIFICO = 900
/** Densita dell'alluminio, kg/m³. */
const DENSITA_STRUTTURA = 2700

/**
 * Capacita termica della PELLE, non dell'intero veicolo.
 *
 * In un rientro si scalda il guscio, non la massa dietro: sono due ordini di
 * grandezza di differenza. Scaldare gli ottocento chili di una capsula come
 * se fossero un unico blocco isotermo darebbe venti gradi in mezzo minuto —
 * cioe nessun rientro pericoloso, mai.
 */
const capacitaPelle = (area: number, spessore: number): number =>
  Math.max(2000, area * spessore * DENSITA_STRUTTURA * CALORE_SPECIFICO)

/**
 * Flusso termico al punto di ristagno, formula di Sutton e Graves:
 *
 *     q̇ = k · √(ρ / R_n) · v³        [W/m²]
 *
 * Il cubo della velocita e tutto: raddoppiare la velocita di rientro
 * moltiplica il flusso per otto. E il motivo per cui si rientra frenando,
 * non tuffandosi.
 */
export function flussoRistagno(densita: number, velocita: number, raggioNaso: number): number {
  if (densita <= 0 || velocita <= 0) return 0
  return K_SUTTON * Math.sqrt(densita / Math.max(0.05, raggioNaso)) * velocita ** 3
}

export interface StatoTermico {
  /** temperatura per parte, K */
  readonly temperature: Record<string, number>
  /** ablativo residuo per parte, kg */
  readonly ablativo: Record<string, number>
}

export const AMBIENTE = 290

export function statoTermicoIniziale(vessel: Vessel, catalog: PartCatalog): StatoTermico {
  const temperature: Record<string, number> = {}
  const ablativo: Record<string, number> = {}
  for (const p of vessel.parts) {
    temperature[p.id] = AMBIENTE
    const scudo = partDef(catalog, p.defId).heatShield
    if (scudo) ablativo[p.id] = scudo.ablativo
  }
  return { temperature, ablativo }
}

export interface RisultatoTermico {
  readonly stato: StatoTermico
  /** parti che hanno superato la temperatura limite e si sono distrutte */
  readonly distrutte: string[]
  /** temperatura piu alta a bordo, K */
  readonly massima: number
}

/**
 * Un passo di riscaldamento.
 *
 * Ogni parte riceve il flusso in proporzione a quanto e ESPOSTA al vento:
 * una parte al riparo dietro lo scudo non si scalda, ed e per questo che
 * l'orientamento in rientro conta piu di qualunque altra cosa.
 */
export function passoTermico(
  stato: StatoTermico,
  vessel: Vessel,
  catalog: PartCatalog,
  body: CelestialBody,
  quota: number,
  airflowBody: Vec3,
  orientamento: { x: number; y: number; z: number; w: number },
  dt: number,
): RisultatoTermico {
  const densita = densityAt(body.atmosphere, quota)
  const v = length(airflowBody)
  const temperature = { ...stato.temperature }
  const ablativo = { ...stato.ablativo }
  const distrutte: string[] = []

  // direzione dell'aria in sistema veicolo, stessa convenzione di aeroForces
  const dir = v > 1e-6 ? normalize(airflowBody) : { x: 0, y: 1, z: 0 }

  /**
   * Schermatura: l'aria incontra per prima la parte con la proiezione minore
   * lungo il flusso. Una parte piu larga davanti mette al riparo quelle
   * dietro — ed e tutto il senso di uno scudo termico.
   */
  const proiezione = new Map<string, number>()
  for (const p of vessel.parts) proiezione.set(p.id, dot(p.position, dir))
  const schermata = (id: string, raggio: number): boolean => {
    const mia = proiezione.get(id)!
    for (const altro of vessel.parts) {
      if (altro.id === id) continue
      if (proiezione.get(altro.id)! >= mia) continue
      const defAltro = partDef(catalog, altro.defId)
      if (shapeRadius(defAltro.shape) < raggio * 0.95) continue
      const scostamento = Math.hypot(altro.position.x - 0, altro.position.z - 0)
      if (scostamento < raggio + shapeRadius(defAltro.shape)) return true
    }
    return false
  }

  for (const p of vessel.parts) {
    const def = partDef(catalog, p.defId)
    const raggio = Math.max(0.05, shapeRadius(def.shape))
    const area = Math.PI * raggio * raggio
    const spessore = (def.material ?? ALLUMINIO).wallThickness
    const capacita = capacitaPelle(area, spessore)

    // esposizione: quanto la parte offre la faccia al flusso. Il fattore
    // decisivo non e questo ma la SCHERMATURA: una capsula dietro il suo
    // scudo prende una frazione del calore, ed e per questo che si rientra
    // con lo scudo davanti.
    const asse = rotate(p.rotation, { x: 0, y: 1, z: 0 })
    const frontale = Math.abs(dot(dir, asse))
    const esposizione = (schermata(p.id, raggio) ? 0.12 : 1) * Math.max(0.15, frontale)

    const flusso = flussoRistagno(densita, v, raggio) * area * esposizione
    let energia = flusso * dt

    // l'ablativo consuma se stesso invece di lasciar salire la temperatura
    const scudo = def.heatShield
    if (scudo && (ablativo[p.id] ?? 0) > 0 && energia > 0) {
      const consumabile = Math.min(ablativo[p.id]!, energia / scudo.capacita)
      ablativo[p.id] = ablativo[p.id]! - consumabile
      energia -= consumabile * scudo.capacita
    }

    const T = temperature[p.id] ?? AMBIENTE
    const irraggiato = STEFAN * EMISSIVITA * area * (T ** 4 - AMBIENTE ** 4) * dt
    const nuova = T + (energia - irraggiato) / capacita
    temperature[p.id] = Math.max(AMBIENTE, nuova)

    if (temperature[p.id]! > def.maxTemp) distrutte.push(p.id)
  }

  return {
    stato: { temperature, ablativo },
    distrutte,
    massima: Math.max(...Object.values(temperature)),
  }
}
